import { useContext, createContext, useCallback, useState, useMemo, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import type { DraftDealProduct } from '@makemydeal/dr-dash-types';
import type { ManualProduct } from '@makemydeal/dr-shared-types';
import type { onDirtyCheckType } from '@makemydeal/dr-dash-components';

import { useManualVpp } from '../vehicleProtectionCard/useManualVpp';
import { useManualProductsTable } from './useManualProductsTable';
import type { TableBuild, TableBuildRow } from './types/table';

type ManualVehicleProtectionProps = {
    onDirtyCheck: onDirtyCheckType;
    shouldTriggerValidation?: boolean;
    children?: ReactNode;
};

export function ManualVPProvider({ children, shouldTriggerValidation, onDirtyCheck }: ManualVehicleProtectionProps) {
    const [editableManualProductIds, setEditableManualProducts] = useState<string[]>([]);

    const editingManualProducts = editableManualProductIds.length > 0;

    const {
        internalManualProducts,
        addEmptyInternalManualProduct,
        removeManualProduct,
        onUpdateManualProductSingleInput,
        onChangeManualProduct
    } = useManualVpp(onDirtyCheck);

    const manualProducts: DraftDealProduct[] = internalManualProducts.map((product) => {
        return {
            ...product,
            isManualProduct: true,
            productProviderName: undefined
        };
    });

    const onManualProductAdd = useCallback(() => {
        const productId = addEmptyInternalManualProduct();
        const newEditableProducts = [...editableManualProductIds, productId];
        setEditableManualProducts(newEditableProducts);
    }, [editableManualProductIds, addEmptyInternalManualProduct, setEditableManualProducts]);

    const onManualProductEdit = useCallback(
        (productId: string) => {
            const newEditableProducts = [...editableManualProductIds, productId];
            setEditableManualProducts(newEditableProducts);
        },
        [editableManualProductIds]
    );

    const onManualProductDelete = useCallback(
        (productId: string) => {
            removeManualProduct(productId);
            const newEditableProducts = editableManualProductIds.filter((id) => id !== productId);
            setEditableManualProducts(newEditableProducts);
        },
        [editableManualProductIds]
    );

    const onManualProductSave = useCallback(
        (productId: string) => {
            // Payment must run: only a successful payment clears payment.error, which blocks Save Deal.
            const product = internalManualProducts.find((p) => p.id === productId);
            if (product) {
                onChangeManualProduct(product, true, false);
            }

            const newEditableProducts = editableManualProductIds.filter((id) => id !== productId);
            setEditableManualProducts(newEditableProducts);
        },
        [editableManualProductIds, internalManualProducts]
    );

    const getManualProductById = useCallback(
        (productId: string) => {
            return internalManualProducts.find((p) => p.id === productId);
        },
        [internalManualProducts]
    );

    const { buildTableDataRow, memoizedOnDeleteProduct, invalidProductIds } = useManualProductsTable({
        products: internalManualProducts,
        shouldTriggerValidation,
        onDeleteProduct: onManualProductDelete,
        onChangeProduct: onChangeManualProduct,
        onUpdateProductSingleInput: onUpdateManualProductSingleInput
    });

    const hasAutoOpenedInvalidProducts = useRef(false);
    useEffect(() => {
        if (!hasAutoOpenedInvalidProducts.current && invalidProductIds.length > 0) {
            setEditableManualProducts(invalidProductIds);
            hasAutoOpenedInvalidProducts.current = true;
        }
    }, [invalidProductIds]);

    const tableBuild: TableBuild = useMemo(() => {
        return internalManualProducts.reduce((table, product) => {
            const productId = product.id;
            return {
                ...table,
                [productId]: buildTableDataRow(product)
            };
        }, {});
    }, [internalManualProducts, buildTableDataRow]);

    const getTableRowByProductId = useCallback(
        (productId: string) => {
            return tableBuild[productId];
        },
        [tableBuild]
    );

    const value = {
        manualProducts,
        editingManualProducts,
        editableManualProductIds,
        getManualProductById,
        getTableRowByProductId,
        onManualProductAdd,
        onManualProductEdit,
        onManualProductDelete: memoizedOnDeleteProduct,
        onManualProductSave
    };

    return <VehicleProtectionManualContext.Provider value={value}>{children}</VehicleProtectionManualContext.Provider>;
}

export type ManualVehicleProtectionContextValue = {
    manualProducts: DraftDealProduct[];
    onManualProductAdd: () => void;
    onManualProductEdit: (productId: string) => void;
    onManualProductDelete: (productId: string) => void;
    onManualProductSave: (productId: string) => void;
    getManualProductById: (productId: string) => ManualProduct | undefined;
    getTableRowByProductId: (productId: string) => TableBuildRow | undefined;
    editingManualProducts: boolean;
    editableManualProductIds: string[];
};

const VehicleProtectionManualContext = createContext<ManualVehicleProtectionContextValue | undefined>(undefined);

export const useManualVehicleProtection = () => {
    const ctx = useContext(VehicleProtectionManualContext);
    if (!ctx) throw new Error('useVehicleProtectionManualContext must be used within VehicleProtectionManualProvider');
    return ctx;
};
